import { asksDemand, demandMade, type IncidentTypeId, type Intake } from "./intake"

/**
 * NIST Cybersecurity Framework 2.0 tie-in for the "after the incident" step.
 * Function and category names are NIST's (CSF 2.0, February 2024). We stay at category level on purpose:
 * it's accurate, plain enough for a first roadmap, and what a framework crosswalk maps onto.
 * https://www.nist.gov/cyberframework
 */
export type CsfFunctionId = "GV" | "ID" | "PR" | "DE" | "RS" | "RC"

export interface CsfFunction { id: CsfFunctionId; name: string; plain: string }
export const CSF_FUNCTIONS: CsfFunction[] = [
  { id: "GV", name: "Govern", plain: "Who is responsible, what the rules are, and how you manage cyber risk." },
  { id: "ID", name: "Identify", plain: "Knowing what you have and what could go wrong." },
  { id: "PR", name: "Protect", plain: "The safeguards that stop or limit an attack." },
  { id: "DE", name: "Detect", plain: "Noticing something is wrong quickly." },
  { id: "RS", name: "Respond", plain: "What you do when something happens." },
  { id: "RC", name: "Recover", plain: "Getting back to normal, safely." },
]

export interface CsfCategory { id: string; fn: CsfFunctionId; name: string }
export const CSF_CATEGORIES: CsfCategory[] = [
  { id: "GV.OC", fn: "GV", name: "Organizational Context" },
  { id: "GV.RM", fn: "GV", name: "Risk Management Strategy" },
  { id: "GV.RR", fn: "GV", name: "Roles, Responsibilities, and Authorities" },
  { id: "GV.PO", fn: "GV", name: "Policy" },
  { id: "GV.OV", fn: "GV", name: "Oversight" },
  { id: "GV.SC", fn: "GV", name: "Cybersecurity Supply Chain Risk Management" },
  { id: "ID.AM", fn: "ID", name: "Asset Management" },
  { id: "ID.RA", fn: "ID", name: "Risk Assessment" },
  { id: "ID.IM", fn: "ID", name: "Improvement" },
  { id: "PR.AA", fn: "PR", name: "Identity Management, Authentication, and Access Control" },
  { id: "PR.AT", fn: "PR", name: "Awareness and Training" },
  { id: "PR.DS", fn: "PR", name: "Data Security" },
  { id: "PR.PS", fn: "PR", name: "Platform Security" },
  { id: "PR.IR", fn: "PR", name: "Technology Infrastructure Resilience" },
  { id: "DE.CM", fn: "DE", name: "Continuous Monitoring" },
  { id: "DE.AE", fn: "DE", name: "Adverse Event Analysis" },
  { id: "RS.MA", fn: "RS", name: "Incident Management" },
  { id: "RS.AN", fn: "RS", name: "Incident Analysis" },
  { id: "RS.CO", fn: "RS", name: "Incident Response Reporting and Communication" },
  { id: "RS.MI", fn: "RS", name: "Incident Mitigation" },
  { id: "RC.RP", fn: "RC", name: "Incident Recovery Plan Execution" },
  { id: "RC.CO", fn: "RC", name: "Incident Recovery Communication" },
]
const CAT = new Map(CSF_CATEGORIES.map((c) => [c.id, c]))

export interface Focus { category: string; why: string }

/** Where each kind of incident usually points. Plain-language "why", general practice, not legal advice. */
export const FOCUS_BY_TYPE: Record<IncidentTypeId, Focus[]> = {
  ransomware: [
    { category: "PR.DS", why: "Keep backups that an attacker can't reach, and test restoring from them, so you aren't forced to consider paying." },
    { category: "PR.AA", why: "Require multi-factor authentication and limit powerful admin accounts." },
    { category: "PR.IR", why: "Separate systems so one compromise can't reach everything." },
    { category: "DE.CM", why: "Watch for intruders so you find them before they lock your systems." },
    { category: "RC.RP", why: "Write down and practice the order you'd restore things in." },
  ],
  "account-compromise": [
    { category: "PR.AA", why: "Turn on multi-factor authentication everywhere, and review who has access to what." },
    { category: "PR.AT", why: "Train people to spot phishing and to report it fast, with no blame." },
    { category: "DE.CM", why: "Alert on unusual sign-ins, forwarding rules, and new app permissions." },
    { category: "DE.AE", why: "Decide who looks at alerts, and how quickly." },
  ],
  "data-exposed": [
    { category: "PR.PS", why: "Check configurations and control changes so storage and databases aren't left open." },
    { category: "PR.DS", why: "Protect sensitive data where it's stored and where it travels." },
    { category: "ID.AM", why: "Keep an inventory of where your data and systems are, so nothing is forgotten." },
    { category: "ID.RA", why: "Review risks regularly, including what you put on the internet." },
  ],
  "lost-device": [
    { category: "PR.DS", why: "Encrypt laptops and phones so a lost device doesn't expose what's on it." },
    { category: "PR.PS", why: "Manage devices so you can lock or wipe them remotely." },
    { category: "ID.AM", why: "Know which devices you have and who holds them." },
  ],
  vendor: [
    { category: "GV.SC", why: "Know which vendors hold your data, what they must tell you after an incident, and how fast." },
    { category: "ID.RA", why: "Include vendors in your risk reviews." },
    { category: "PR.AA", why: "Limit and review the access you give vendors." },
  ],
  insider: [
    { category: "PR.AA", why: "Give people only the access they need, and remove it promptly when roles change or they leave." },
    { category: "DE.CM", why: "Monitor important systems for unusual access and bulk copying, within your policies and the law." },
    { category: "GV.RR", why: "Define who handles HR, legal, and security together, before you need to." },
    { category: "PR.AT", why: "Make expectations and reporting channels clear." },
  ],
  "bec-fraud": [
    { category: "GV.PO", why: "Set a rule that payment or bank-detail changes are confirmed by phone with a known number." },
    { category: "PR.AT", why: "Train the people who handle payments to spot fake requests." },
    { category: "PR.AA", why: "Protect email accounts with multi-factor authentication." },
    { category: "DE.AE", why: "Flag lookalike domains and unusual payment requests." },
  ],
  unsure: [
    { category: "GV.RR", why: "Name who is responsible for cybersecurity." },
    { category: "ID.AM", why: "Start with a simple list of your systems, accounts, and data." },
    { category: "RS.MA", why: "Write a one-page incident response plan with contacts." },
    { category: "PR.AA", why: "Turn on multi-factor authentication for email and admin accounts." },
  ],
}

export interface FocusItem { category: CsfCategory; reasons: string[] }

/**
 * Categories this incident points to: the usual ones for the incident type, plus anything the person's own
 * answers highlight (no backups, no counsel or response firm on hand, sensitive data, a demand).
 */
export function csfFocus(i: Intake): FocusItem[] {
  const out = new Map<string, FocusItem>()
  const add = (id: string, why: string) => {
    const category = CAT.get(id)
    if (!category) return
    const cur = out.get(id) ?? { category, reasons: [] }
    if (!cur.reasons.includes(why)) cur.reasons.push(why)
    out.set(id, cur)
  }
  for (const f of FOCUS_BY_TYPE[i.incidentType ?? "unsure"]) add(f.category, f.why)
  if (i.backupsIntact === "no") { add("PR.DS", "You said your backups weren't intact or were reachable. Make backups safe and tested."); add("RC.RP", "Write down how you'd restore, and practice it.") }
  if (i.hasCounsel === "no" || i.hasIrProvider === "no") add("RS.MA", "You didn't have a lawyer or response firm to call. Write an incident response plan and line up help before you need it.")
  if (i.hasInsurance === "no") add("GV.RM", "You didn't have cyber insurance. Decide how much cyber risk to carry, transfer, or reduce.")
  if (i.dataTypes.some((d) => d !== "none-known")) add("PR.DS", "Sensitive data was involved. Know where it is and protect it.")
  if (i.unauthorizedAccess === "yes") add("DE.CM", "Someone got in. Monitoring helps you find the next attempt sooner.")
  if (asksDemand(i.incidentType) && demandMade(i)) add("RS.MA", "A demand was made. Decide ahead of time who decides what, and who to call, before the next one.")
  return [...out.values()]
}

// ---- self-check: one question per function ----------------------------------------------------
export type CsfAnswer = "yes" | "partly" | "no"
export const CSF_ANSWERS: { id: CsfAnswer; label: string }[] = [
  { id: "yes", label: "Yes" },
  { id: "partly", label: "Partly" },
  { id: "no", label: "No" },
]

export interface CsfCheckItem { fn: CsfFunctionId; statement: string; next: string[] }
export const CSF_CHECK: CsfCheckItem[] = [
  { fn: "GV", statement: "Someone is clearly responsible for cybersecurity, and we have written security rules that people actually follow.",
    next: ["Name one person who owns cybersecurity, even if it's part of their job.", "Write a short security policy: passwords and MFA, devices, data, vendors, and who to call.", "Decide how much cyber risk you'll accept and how you'll insure the rest."] },
  { fn: "ID", statement: "We know what devices, accounts, systems, and data we have, and which ones matter most.",
    next: ["List your systems, accounts, and where sensitive data lives.", "Mark the few things the business can't run without.", "Review the risks to those things at least once a year."] },
  { fn: "PR", statement: "We use multi-factor authentication, keep systems updated, encrypt laptops and phones, and keep tested backups.",
    next: ["Turn on multi-factor authentication for email, admin, and remote access first.", "Encrypt laptops and phones, and make sure you can lock or wipe them.", "Keep backups that can't be reached from your network, and test a restore."] },
  { fn: "DE", statement: "Someone would notice unusual activity quickly, through monitoring or alerts that a person actually reads.",
    next: ["Turn on sign-in and admin alerts in your email and cloud accounts.", "Decide who reads alerts, and what they do next.", "Consider endpoint detection and monitoring, in-house or from a provider."] },
  { fn: "RS", statement: "We have a written incident response plan with contacts, and we've practiced it.",
    next: ["Write a one-page plan: who leads, who to call (lawyer, insurer, response firm), and how you'll communicate.", "Keep a printed contact list somewhere that survives an outage.", "Run a 30-minute practice scenario once or twice a year."] },
  { fn: "RC", statement: "We can restore important systems from tested backups, and we know the order we'd bring things back.",
    next: ["Write down the restore order for your critical systems.", "Test restoring from backup, not just taking it.", "Decide how you'd keep working during an outage."] },
]

export type CsfCheck = Partial<Record<CsfFunctionId, CsfAnswer>>

const SCORE: Record<CsfAnswer, number> = { yes: 2, partly: 1, no: 0 }

export interface CsfResult { fn: CsfFunction; answer: CsfAnswer | null; score: number | null; next: string[] }

/** Results in CSF order, plus the order to work on them: weakest first, unanswered last. */
export function csfResults(check: CsfCheck): { results: CsfResult[]; priority: CsfResult[]; answered: number } {
  const results = CSF_FUNCTIONS.map((fn) => {
    const answer = check[fn.id] ?? null
    return { fn, answer, score: answer ? SCORE[answer] : null, next: CSF_CHECK.find((c) => c.fn === fn.id)?.next ?? [] }
  })
  const priority = results
    .filter((r) => r.score !== null && r.score < 2)
    .sort((a, b) => (a.score as number) - (b.score as number))
  return { results, priority, answered: results.filter((r) => r.answer).length }
}
